import styled, { css } from "styled-components";

import TriggerButton from "components/OverflowMenu/TriggerButton";

import NounLogoBrandmark from "core/ReusableIcons/NounLogoBrandmark";
import Search from "core/ReusableIcons/Search";

import Link from "core/Link";
import { Section } from "core/Layout";
import {
    mobileHeaderHeight,
    desktopHeaderHeight,
    desktopHeaderHeightCollapsed
} from "core/SiteHeader/constants";
import { easingSeconds } from "core/animation";
import Flexbox from "core/Flexbox";
import { A } from "core/Typography";
import Button from "core/Button";
import { OldGrid, Row, Col } from "core/oldGrid";
import { regular as regularFontWeight } from "core/fontStyles";
import breakpoints from "core/breakpoints";
import colors, { focusBorder } from "core/colors";

import { convertPxToRem } from "frontend/components/helpers";

export const Logo = styled(NounLogoBrandmark)`
    width: 2.375rem;
    transition: opacity ${easingSeconds};

    &:hover {
        opacity: 0.6;
    }
`;

export const HeaderLogoLink = styled.a`
    display: inline-block;

    svg {
        transition: opacity ${easingSeconds};
    }
    &:hover svg {
        opacity: 0.6;
    }

    /* from mobile to desktop breakpoint: */
    @media (min-width: ${breakpoints.zero.rem}) and (max-width: ${breakpoints
            .beforeDesktop.rem}) {
        ${({ isHeroSearchV2 }) =>
            isHeroSearchV2 &&
            css`
                height: 0.625rem; /* 10px */
                margin-top: -0.125rem; /* -2px */
            `}
        svg {
            margin-right: 1.25rem; /* 20px */
        }
    }

    /* mobile: */
    @media (min-width: ${breakpoints.zero.rem}) and (max-width: ${breakpoints
            .beforeTablet.rem}) {
        svg {
            width: 38px;
            height: 10px;
        }
    }

    /* Tablet & Desktop: */
    @media (min-width: ${breakpoints.tablet.rem}) {
        height: 23px; /* height of desktop logo */
        svg {
            width: 84px;
            height: 23px;
        }
    }
`;

// search
export const SearchInput = styled.input.attrs({
    type: "search"
})`
    width: 100%;
    border: 3px solid ${colors.primary.diamond};
    transition: all ${easingSeconds};
    border-radius: 0;
    -webkit-appearance: none !important;
    -webkit-logical-width: -webkit-fill-available;
    font-family: inherit;
    color: ${colors.secondary.onyx};
    font-size: 1rem; /* 16px to avoid zoom on mobile devices */
    padding: 0.875rem 1rem 0.875rem 2.625rem;
    margin: 0;
    box-sizing: border-box !important; /* overrides main.css */
    ${regularFontWeight}

    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        padding: 0.375rem 0.625rem 0.375rem 3.125rem;
        /* ^^ 6px 10px 6px 50px - in the design, placeholder text is ~53px to the right of left side of input, making space for mobile search type toggle; then we subtract 3px for input border.  */
    }

    @media (min-width: ${breakpoints.tablet.rem}) {
        /* padding for search input paired with toggle; more padding on the left so we make space for the toggle & on the right so we make space for the submit button */
        padding: 0.875rem 2.5rem 0.875rem 9rem; /* 14px 40px 14px 144px */
        height: 2.625rem; /* 42px - set height to prevent variable height on different browsers */
    }

    &::placeholder {
        opacity: 1;
        color: ${colors.secondary.oolong};
    }

    &:hover {
        border-color: ${colors.secondary.oolong};
    }

    &:focus {
        ${focusBorder};
        outline: none;
        &::placeholder {
            color: transparent;
            /* hides placeholder text on input focus */
        }
    }
`;

export const SearchWrapper = styled.div`
    position: relative;
    ${({ isHeroSearchV2 }) =>
        isHeroSearchV2 &&
        css`
            display: none;
        `}
`;

// non-mobile content
export const NonMobileTopRow = styled(Row)``;

export const MainLinksCol = styled(Col)`
    transition: opacity ${easingSeconds};
    [aria-hidden="true"] {
        display: none;
    }
`;

export const ProfileMenuCol = styled(Col)`
    transition: margin-top ${easingSeconds};
`;

export const SearchContainerRow = styled(Row)`
    transition: margin ${easingSeconds};

    /* prettier-ignore */
    ${Col}:first-of-type {
        height: 23px; /* height of desktop logo */
    }
    
    /* prettier-ignore */
    ${Col}:nth-child(2) {
        min-height: 42px;
    }
`;

export const TextLinkGrid = styled(OldGrid)`
    overflow-x: visible;
`;

export const MainLinks = styled(Flexbox)`
    ${Link} {
        white-space: nowrap;

        &:not(:last-of-type) {
            margin-right: ${convertPxToRem({ px: 16 })};
        }
    }
`;

export const NonMobileContentWrapper = styled.div`
    ${NonMobileTopRow} {
        margin-bottom: 1.625rem;
        height: 24px;
    }

    ${Logo} {
        width: 5.25rem;
    }

    ${SearchWrapper} {
        width: 100%;
    }

    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        display: none;
    }
`;

// mobile content
export const LogoAndSearch = styled(Flexbox)`
    ${Logo} {
        margin-right: 1.25rem;
    }

    ${SearchWrapper} {
        flex: 1;
    }
`;

export const MenuAndAvatar = styled(Flexbox)`
    ${TriggerButton} {
        ${({ loggedOut }) =>
            loggedOut &&
            css`
                /* Mobile-only: */
                @media (max-width: ${breakpoints.beforeTablet.rem}) {
                    margin-left: 1rem; /* 16px */
                    margin-right: 0;
                }
            `}
    }

    /* up to Tablet size: */
    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        margin-top: -2px;
    }
`;

export const HeaderRow = styled(Row)``;

export const MobileContentWrapper = styled(OldGrid)`
    display: none;

    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        display: block;
        overflow-x: visible;

        ${HeaderRow} {
            height: 2.313rem; /* 37px - prevents jumping of header when scrolling, especially in terms of hero to header search input */
        }
    }
`;

// site header
export const StyledSiteHeader = styled(Section)`
    position: fixed;
    z-index: 9999;
    transition: background ${easingSeconds};

    /* Mobile: */
    padding-top: 0.625rem; /* 10px */
    padding-bottom: 0.625rem; /* 10px */
    height: ${mobileHeaderHeight}px;
    /* end mobile styles */

    ${({ transparent }) =>
        transparent &&
        css`
            background-color: transparent;
            border-bottom: 0;
        `}

    ${({ heroSearchBgColor }) =>
        `background-color: ${
            heroSearchBgColor ? heroSearchBgColor : colors.primary.snow
        }; border-bottom: 0;`};


    ${({ transparent }) =>
        !transparent &&
        css`
            background-color: ${colors.primary.snow};
            border-bottom: 1px solid #d8d8d8;
        `}


    /* for new hero search/navbar combo */
    ${({ isHeroSearchV2, heroSearchBgColor }) =>
        isHeroSearchV2 &&
        css`
            background-color: ${heroSearchBgColor
                ? heroSearchBgColor
                : colors.primary.snow};
            border-bottom: 0;
        `}
    //
    
    /* Tablet & Desktop */
    @media (min-width: ${breakpoints.tablet.rem}) {
        padding-top: 2rem;
        padding-bottom: 2rem;
        height: ${desktopHeaderHeight}px;
        
        ${ProfileMenuCol} {
            margin-top: 0;
        }
        
        &.scrolledDown {
            height: ${desktopHeaderHeightCollapsed}px;
            ${ProfileMenuCol} {
                margin-top: 8px;
            }
        }

        &.scrolledUp {
            height: ${desktopHeaderHeight}px;
            ${ProfileMenuCol} {
                margin-top: 0;
            }
        }
    }
        
    ${({ isHeroSearchV2 }) =>
        isHeroSearchV2 &&
        css`
            /* changes to header UI when scrolling down the page: */
            &.scrolledDown {
                /* SiteHeader itself: */
                border-bottom: 1px solid #d8d8d8;

                /* overflow menu: */
                ${TriggerButton} {
                    path {
                        fill: ${colors.secondary.oolong};
                    }

                    &:hover {
                        background-color: ${colors.primary.scheifer};

                        path {
                            fill: ${colors.secondary.orca};
                        }
                    }
                }

                /* NP Logo: */
                a svg path {
                    fill: ${colors.secondary.onyx};
                }
            }

            /* changes to header UI when scrolled to top of page: */
            &.scrolledUp {
                /* SiteHeader itself: */
                border-bottom: 0;

                /* NP logo */
                a svg path {
                    fill: ${colors.primary.snow};
                }
            }

            /* when scrolling down the page, hide hero search & show header search */
            .scrolledDownSearch {
                form {
                    display: block;
                }

                .search-type-toggle-dropdown {
                    display: block;
                }
            }

            /* when scrolling up the page, show hero search & hide header search */
            .scrolledUpSearch form,
            .MuiToggleButtonGroup-root,
            .search-type-toggle-dropdown {
                display: none;
            }
        `}
`;

// new search button:
export const SubmitButton = styled(Button)`
    border-radius: 0;
    position: absolute;
    top: 0.1875rem; /* 3px */
    right: 0.1875rem; /* 3px */
    cursor: pointer;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    ${({ isDisabled }) =>
        isDisabled &&
        css`
            background-color: ${colors.secondary.onyx};
            cursor: not-allowed;
        `}

    ${Search} {
        width: ${convertPxToRem({ px: 20 })};
        height: ${convertPxToRem({ px: 20 })};
        top: 0;
        margin-right: 0;
    }

    /* styles for hero: */
    #hero-search-wrapper & {
        height: ${convertPxToRem({ px: 62 })};
        min-width: ${convertPxToRem({ px: 62 })};

        /* fixes safari issues */
        @media not all and (min-resolution: 0.001dpcm) {
            @supports (-webkit-appearance: none) and (stroke-color: transparent) {
                right: 0.0625rem; /* 1px */
            }
        }
    }

    /* styles for header: */
    #site-header & {
        height: ${convertPxToRem({ px: 36 })};
        min-width: ${convertPxToRem({ px: 36 })};
        width: ${convertPxToRem({ px: 36 })};
    }

    /* Hide button on mobile devices: */
    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        display: none;
    }

    /* CSS specific to iOS devices: https://stackoverflow.com/a/47818418 */
    @supports (-webkit-touch-callout: none) {
        right: 0.125rem; /* 2px */
    }
`;
